/**
 * Analytics Helper
 * 
 * Shared utilities for tracking analytics events with:
 * - Payload validation
 * - PII redaction
 * - Consistent field formatting
 * - Type safety
 */

import { trackEvent as rawTrackEvent } from '../contexts/PostHogProvider';
import { EVENT_SOURCES } from '../constants/analyticsEvents';

/**
 * PII-sensitive field names that should be redacted
 */
const PII_FIELDS = new Set([
  'email',
  'name',
  'password',
  'phone',
  'address',
  'ssn',
  'credit_card',
  'ip_address',
]);

/**
 * Redact PII from payload
 * @param {Object} payload - Event payload
 * @returns {Object} Sanitized payload
 */
function redactPII(payload) {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(payload)) {
    if (PII_FIELDS.has(key.toLowerCase())) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = redactPII(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Validate required fields for onboarding/auth events
 * @param {Object} payload - Event payload
 * @param {Array<string>} requiredFields - Required field names
 * @throws {Error} If required fields are missing
 */
function validatePayload(payload, requiredFields = []) {
  const missing = requiredFields.filter(field => {
    const value = payload[field];
    return value === undefined || value === null || value === '';
  });

  if (missing.length > 0) {
    console.warn(`[Analytics] Missing required fields: ${missing.join(', ')}`, payload);
  }
}

/**
 * Track an analytics event with validation and PII redaction
 * 
 * @param {string} eventName - Event name (use constants from analyticsEvents.js)
 * @param {Object} payload - Event payload
 * @param {string} payload.tenant_id - Tenant ID (required for most events)
 * @param {string} payload.user_id - User ID (required for most events)
 * @param {string} payload.role - User role (required for most events)
 * @param {string} [payload.step_id] - Onboarding step ID (for step events)
 * @param {string} [payload.flow_variant] - Flow variant for A/B testing
 * @param {number} [payload.duration_ms] - Duration in milliseconds
 * @param {string} [payload.error_code] - Error code for failure events
 * @param {string} [payload.source] - Event source (web/extension)
 * @param {Object} [options] - Additional options
 * @param {Array<string>} [options.requiredFields] - Fields to validate
 * @param {boolean} [options.skipPIIRedaction] - Skip PII redaction (use with caution)
 */
export function trackAnalyticsEvent(eventName, payload = {}, options = {}) {
  const {
    requiredFields = [],
    skipPIIRedaction = false,
  } = options;

  // Validate payload
  validatePayload(payload, requiredFields);

  // Add default source if not provided
  const enrichedPayload = {
    source: EVENT_SOURCES.WEB,
    ...payload,
    timestamp: new Date().toISOString(),
  };

  // Redact PII unless explicitly skipped
  const sanitizedPayload = skipPIIRedaction ? enrichedPayload : redactPII(enrichedPayload);

  // Track event
  rawTrackEvent(eventName, sanitizedPayload);

  // Optional: Log in development
  if (import.meta.env.DEV) {
    console.log('[Analytics]', eventName, sanitizedPayload);
  }
}

/**
 * Track authentication event
 * Helper for auth_login_started, auth_login_succeeded, auth_login_failed
 */
export function trackAuthEvent(eventName, { userId, role, tenantId, errorCode, flowVariant, durationMs }) {
  trackAnalyticsEvent(eventName, {
    tenant_id: tenantId,
    user_id: userId,
    role: role,
    error_code: errorCode,
    flow_variant: flowVariant,
    duration_ms: durationMs,
  });
}

/**
 * Track onboarding flow event
 * Helper for onboarding_flow_started
 */
export function trackOnboardingFlowStarted({ userId, role, tenantId, flowVariant }) {
  trackAnalyticsEvent('onboarding_flow_started', {
    tenant_id: tenantId,
    user_id: userId,
    role: role,
    flow_variant: flowVariant,
  }, {
    requiredFields: ['tenant_id', 'user_id', 'role'],
  });
}

/**
 * Track onboarding step event
 * Helper for onboarding_step_viewed, onboarding_step_completed, onboarding_step_failed, onboarding_step_skipped
 */
export function trackOnboardingStepEvent(eventName, { userId, role, tenantId, stepId, flowVariant, durationMs, errorCode }) {
  trackAnalyticsEvent(eventName, {
    tenant_id: tenantId,
    user_id: userId,
    role: role,
    step_id: stepId,
    flow_variant: flowVariant,
    duration_ms: durationMs,
    error_code: errorCode,
  }, {
    requiredFields: ['tenant_id', 'user_id', 'role', 'step_id'],
  });
}

/**
 * Track onboarding sync event
 * Helper for onboarding_sync_started, onboarding_sync_completed, onboarding_sync_failed
 */
export function trackOnboardingSyncEvent(eventName, { userId, role, tenantId, syncType, durationMs, errorCode, itemCount }) {
  trackAnalyticsEvent(eventName, {
    tenant_id: tenantId,
    user_id: userId,
    role: role,
    sync_type: syncType,
    duration_ms: durationMs,
    error_code: errorCode,
    item_count: itemCount,
  }, {
    requiredFields: ['tenant_id', 'user_id', 'role'],
  });
}

/**
 * Track first proposal creation
 */
export function trackFirstProposalCreated({ userId, role, tenantId, proposalId, jobId, hasCoverLetter, durationMs }) {
  trackAnalyticsEvent('onboarding_first_proposal_created', {
    tenant_id: tenantId,
    user_id: userId,
    role: role,
    proposal_id: proposalId,
    job_id: jobId,
    has_cover_letter: hasCoverLetter,
    duration_ms: durationMs,
  }, {
    requiredFields: ['tenant_id', 'user_id', 'role'],
  });
}

export default {
  trackAnalyticsEvent,
  trackAuthEvent,
  trackOnboardingFlowStarted,
  trackOnboardingStepEvent,
  trackOnboardingSyncEvent,
  trackFirstProposalCreated,
};
